import { Component, NgZone, ElementRef } from '@angular/core';
import { Events } from '@services/events/events.service';
import { ActivatedRoute } from '@angular/router';

import {
  IHeaderSetting,
  FilterElementService,
  TableService,
  ResizeHandlerService,
  LoadingService,
  Module
} from '@services';
import { ObservationTableService } from '@services/observationTableService/observation-table-service.service';

import { TranslateService } from '@ngx-translate/core';
import { concat, each, filter, find, first } from 'lodash';

@Component({
  selector: 'app-process-table',
  templateUrl: './process-table.page.html',
  styleUrls: ['./process-table.page.scss'],
})
export class ProcessTablePage {

  public headerSetting: IHeaderSetting = {
    title: this.translate.instant('SHARED.Process_Improvements'),
    filter: {
      showFilter: true,
      isActive: false
    },
    showSearchField: true,
    selectDropDown: {
      dataNavMenuKey: Module.OBSERVATIONS,
      option: {
        minimumResultsForSearch: Infinity,
        placeholder: this.translate.instant('SHARED.COACHING_TABLE_placeholder'),
        width: '15em',
        closeOnSelect: true,
        sorter: (data) => data
      },
      defaultVal: null
    }
  };

  // prepare tabs
  public tabs: any = [
    {
      name: this.translate.instant('SHARED.Submitted'),
      selected: true,
      active: true,
      count: 0,
      id: 'processSubmitted'
    },
    {
      name: this.translate.instant('SHARED.Assigned'),
      selected: false,
      active: true,
      count: 0,
      id: 'processAssigned'
    },
    {
      name: this.translate.instant('SHARED.Implemented'),
      selected: false,
      active: true,
      count: 0,
      id: 'processImplemented'
    },
    {
      name: this.translate.instant('SHARED.Closed'),
      selected: false,
      active: true,
      count: 0,
      id: 'processClosed'
    }
  ];

  public filterObject = this.observationTableService.filterObject;
  public filterComps: any = [];
  public showFilterBody = false;
  public preSelected: any = first(this.tabs);
  public searchField: any = '';
  private tableData: any = {};
  private menuItem: any = ['location', 'team', 'user', 'zone', 'timespan'];
  private filterString: string = null;
  private clearFilter = false;
  private isNeededTableRefreshing: any = {};

  constructor(
    private observationTableService: ObservationTableService,
    private events: Events,
    private zone: NgZone,
    private filterElem: FilterElementService,
    private tableService: TableService,
    private resizeHandler: ResizeHandlerService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private loadingService: LoadingService,
    private elementRef: ElementRef
  ) {
  }

  ionViewWillLeave() {
    this.resizeHandler.removeResizeHandler('processTable');
    this.events.unsubscribe('ccs:filterObject', this.filterListener);
    this.events.unsubscribe('ccs:clearMenu', this.clearMenuListener);
    this.events.unsubscribe('ccs:observationUpdate', this.observationUpdateListener);
    this.events.unsubscribe('ccs:searchString', this.searchStringListener);

    if (this.clearFilter) {
      this.observationTableService.clearFilterObject();
    }
  }

  ionViewDidEnter() {
    this.searchField = this.observationTableService.filterObject.searchString;
    this.clearFilter = this.route.snapshot.queryParamMap.get('clearFilter') === 'true';
    $('.observation-search-field').val(this.searchField);

    this.events.subscribe('ccs:observationUpdate', this.observationUpdateListener);
    this.events.subscribe('ccs:filterObject', this.filterListener);
    this.events.subscribe('ccs:searchString', this.searchStringListener);
    this.events.subscribe('ccs:clearMenu', this.clearMenuListener);

    this.prepData();
    this.preSelected = find(this.tabs, ['selected', true]);

    const activeType: string = this.route.snapshot.queryParamMap.get('activeTab');
    const activeTab = find(this.tabs, {id: activeType});

    this.filterComps = this.filterElem.buildFilterObjects(this.menuItem, this.observationTableService.filterObject);
    this.headerSetting.filter.isActive = this.observationTableService.filterValuesAvailable(this.filterComps);
    this.resizeHandler.addResizeHandler('processTable', () => this.tableService.handleResize('.dataTables_scrollBody:visible:last', 50, null));

    if (activeTab) {
      this.toggle(activeTab, false);
      this.preSelected = activeTab;
    } else {
      this.buildTableElements(this.preSelected);
      this.markTablesForNextUpdate();
    }
  }

  public toggleFilterBody(val) {
    this.showFilterBody = val;
    // check if custom is active on time span
    const timeBlock: any = find(this.filterComps, {id: 'timespan'});
    if (timeBlock.value[0] === 'custom') {
      $(this.elementRef.nativeElement).find('#timespan select').val([]);
    }
  }

  public toggle(tab, isTabChanging: boolean = true) {
    // find selected first
    const prevObj = find(this.tabs, ['selected', true]);
    prevObj.selected = false;
    this.preSelected = find(this.tabs, ['id', tab.id]);
    this.preSelected.selected = true;
    this.buildTableElements(tab, isTabChanging);

    if (prevObj.id !== tab.id) {
      this.observationTableService.updateRouterActiveTab(tab.id);
    }
  }

  private searchStringListener = (data) => {
    this.observationTableService.filterObject = data; // get filter elem
    this.prepData();
    this.buildTableElements(this.preSelected, false, true, true);
    this.markTablesForNextUpdate();
  };

  private observationUpdateListener = (data) => {
    if (data) {
      this.prepData();
      this.buildTableElements(this.preSelected);
      this.markTablesForNextUpdate();
    }
  };

  private filterListener = (data) => {
    this.observationTableService.filterObject = data; // get filter elem
  };

  private clearMenuListener = (data) => {
    if (data) {
      this.zone.run(() => {
        // tslint:disable-next-line:max-line-length
        this.filterComps = this.filterElem.buildFilterObjects(this.menuItem, this.observationTableService.filterObject);
        let checkFilterActive = false;
        each(this.filterComps, comp => {
          if (comp.value && comp.value.length > 0) {
            checkFilterActive = true;
            return false;
          }
        });
        this.headerSetting.filter.isActive = checkFilterActive;
        this.prepData();

        setTimeout(() => {
          this.buildTableElements(this.preSelected);
          this.markTablesForNextUpdate();
        });
      });
    }
  };

  private prepData() {
    // need to update end time to capture new incoming observaitons
    if (this.observationTableService.filterObject.endTime && this.observationTableService.filterObject.timespan !== 'custom') {
      const endTimeNow = Date.now();
      this.observationTableService.filterObject.endTime = endTimeNow;
    }
    if (!this.filterObject.timespan) {
      this.filterObject.timespan = 'all';
    }
    const rawData = this.observationTableService.getObservation('pi');
    if (rawData.rows.resolved) {
      this.tableData.processClosed = rawData.rows.resolved.items;
    } else {
      this.tableData.processClosed = [];
    }
    if (rawData.rows.fixed) {
      this.tableData.processImplemented = rawData.rows.fixed.items;
    } else {
      this.tableData.processImplemented = [];
    }

    if (rawData.rows.new || rawData.rows.escalated) {
      let openBucket: any = [];
      if (rawData.rows.new) { // if both new and escalated exist, put them in one
        if (rawData.rows.escalated) {
          openBucket = concat(rawData.rows.new.items, rawData.rows.escalated.items);
        } else { // if new is present, but escalated is not.
          openBucket = rawData.rows.new.items;
        }
      } else { // if only escalated is present
        openBucket = rawData.rows.escalated.items;
      }
      this.tableData.processSubmitted = filter(openBucket, data => {
        if (!data.ownerID) {
          return true;
        }
      });

      this.tableData.processAssigned = filter(openBucket, data => {
        if (data.ownerID) {
          return true;
        }
      });
    } else {
      this.tableData.processSubmitted = [];
      this.tableData.processAssigned = [];
    }

    this.tabs = this.observationTableService.fillCount(this.tabs, this.tableData);
    this.filterString = this.filterObject.searchString;
  }

  private buildTableElements(tab, isTabChanging?: boolean, recalculate?: boolean, disableLoading?: boolean) {
    this.observationTableService.buildTableByTab({
      tab,
      tableData: this.tableData,
      filterString: this.filterString,
      loadingState: this.isNeededTableRefreshing,
      isTabChanging,
      recalculate,
      disableLoading
    });
  }

  private markTablesForNextUpdate() {
    each(this.tabs, (tab) => {
      this.isNeededTableRefreshing[tab.id] = true;
    });
  }

}
